// The calendar surface (KSE-0191): renders the CalendarShellSnapshot the
// shared Swift core publishes and dispatches calendar ShellCommands. Holds no
// calendar state of its own — view mode, anchor, selection and layouts all
// come from the snapshot; the only local state is chrome (calendars sidebar
// open/closed).
import { CalendarX2, Loader2 } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";

import { cn } from "@/lib/utils";
import { useCalendarSnapshot, useShell, useShellSnapshot } from "../core/shell";
import type {
  CalendarDate,
  CalendarShellSnapshot,
  CalendarViewMode,
  MailShellSnapshot,
  ShellCommand,
} from "../core/shell-types";
import { AgendaView } from "./AgendaView";
import { CalendarList } from "./CalendarList";
import { CalendarToolbar } from "./CalendarToolbar";
import { EventDetail } from "./EventDetail";
import { firstWeekdayFromLocale, lastSyncedLabel, resolvedTimeZone } from "./format";
import { MonthView } from "./MonthView";
import { Sheet } from "./Sheet";
import { TimeGridView } from "./TimeGridView";
import { useCalendarHistory } from "./useCalendarHistory";

const CALENDARS_CAPABILITY = "urn:ietf:params:jmap:calendars";
const SIDEBAR_STORAGE_KEY = "lately.web.calendar.sidebar";
// The core re-derives `today` and the now indicator only when it recomputes
// (an op or a store change), so the page asks for it once a minute while
// visible (calendarRefreshClock) — aligned to the minute so the now-line
// moves on the minute and `today` rolls over at midnight.
const CLOCK_TICK_MS = 60_000;

function readSidebarPreference(): boolean {
  try {
    return localStorage.getItem(SIDEBAR_STORAGE_KEY) !== "closed";
  } catch {
    return true;
  }
}

function writeSidebarPreference(open: boolean) {
  try {
    localStorage.setItem(SIDEBAR_STORAGE_KEY, open ? "open" : "closed");
  } catch {
    // Preference only.
  }
}

// Whether the active account can have calendars: its session advertises the
// JMAP calendars capability (or the core already flagged supportsCalendars).
// "unknown" while no account/session is known yet.
export function calendarsAvailability(
  snapshot: MailShellSnapshot,
): "available" | "unavailable" | "unknown" {
  const account =
    snapshot.accounts.find(
      (candidate) => candidate.storageKey === snapshot.activeAccountStorageKey,
    ) ?? snapshot.accounts.find((candidate) => candidate.isActive);
  if (!account) {
    return "unknown";
  }
  if (account.supportsCalendars) {
    return "available";
  }
  const capabilities = account.session?.session?.capabilities;
  if (!capabilities) {
    return "unknown";
  }
  return CALENDARS_CAPABILITY in capabilities ? "available" : "unavailable";
}

function isTypingTarget(target: EventTarget | null): boolean {
  const element = target as HTMLElement | null;
  if (!element) {
    return false;
  }
  return (
    element.tagName === "INPUT" ||
    element.tagName === "TEXTAREA" ||
    element.tagName === "SELECT" ||
    element.isContentEditable
  );
}

function CalendarPlaceholder({
  status,
  error,
}: {
  status: "loading" | "unavailable";
  error?: string | null;
}) {
  return (
    <div
      data-calendar-state={status}
      className="flex min-h-0 flex-1 flex-col items-center justify-center gap-2 px-6 text-center text-muted-foreground"
    >
      {status === "unavailable" ? (
        <>
          <CalendarX2 aria-hidden className="h-8 w-8 opacity-60" />
          <p className="font-display text-lg">Calendars are not available for this account</p>
          <p className="max-w-sm text-sm">
            The mail server did not advertise JMAP calendars for this account.
          </p>
        </>
      ) : (
        <>
          <Loader2 aria-hidden className="h-6 w-6 animate-spin opacity-60" />
          <output className="text-sm">Loading calendar…</output>
        </>
      )}
      {error ? (
        <p role="alert" className="text-sm text-destructive">
          {error}
        </p>
      ) : null}
    </div>
  );
}

function CalendarContent({
  calendar,
  compact,
  onSelectDay,
  onSelectOccurrence,
}: {
  calendar: CalendarShellSnapshot;
  compact: boolean;
  onSelectDay: (date: CalendarDate) => void;
  onSelectOccurrence: (key: string) => void;
}) {
  if (calendar.monthLayout && calendar.viewMode === "month") {
    return (
      <MonthView
        snapshot={calendar}
        layout={calendar.monthLayout}
        compact={compact}
        onSelectDay={onSelectDay}
        onSelectOccurrence={onSelectOccurrence}
      />
    );
  }
  if (calendar.weekLayout && (calendar.viewMode === "day" || calendar.viewMode === "week")) {
    return (
      <TimeGridView
        snapshot={calendar}
        layout={calendar.weekLayout}
        compact={compact}
        onSelectDay={onSelectDay}
        onSelectOccurrence={onSelectOccurrence}
      />
    );
  }
  if (calendar.agendaLayout && calendar.viewMode === "agenda") {
    return (
      <AgendaView
        snapshot={calendar}
        layout={calendar.agendaLayout}
        compact={compact}
        onSelectOccurrence={onSelectOccurrence}
      />
    );
  }
  // The core sends exactly one layout per mode; a snapshot without one is
  // in flight (the mode just changed) — keep the frame quiet.
  return <CalendarPlaceholder status="loading" />;
}

// `active`: whether this surface is the one on screen (AppShell); only the
// active surface drives browser history. The shell is currently unmounted
// while inactive, so it defaults to true.
export function CalendarShell({ compact, active = true }: { compact: boolean; active?: boolean }) {
  const shell = useShell();
  const snapshot = useShellSnapshot();
  const calendar = useCalendarSnapshot();
  const [sidebarOpen, setSidebarOpen] = useState(readSidebarPreference);
  const [sheetOpen, setSheetOpen] = useState(false);
  const availability = calendarsAvailability(snapshot);

  const run = useCallback(
    (command: ShellCommand) => {
      shell.command(command).catch((cause: unknown) => {
        console.warn(`Lately: calendar command ${command.op} failed`, cause);
      });
    },
    [shell],
  );

  // Tell the core which zone and week start to lay out for; again whenever
  // the browser's resolved zone changes (travel, OS setting) — checked when
  // the tab comes back to the foreground, where an unchanged zone still gets
  // the clock re-derived (today / the now indicator may have moved while
  // hidden). The first configure is followed by an explicit month view when
  // the core has not published anything yet.
  const configuredZone = useRef<string | null>(null);
  const hadSnapshot = useRef(calendar !== null);
  useEffect(() => {
    const configure = () => {
      const timeZone = resolvedTimeZone();
      if (configuredZone.current === timeZone) {
        run({ op: "calendarRefreshClock" });
        return;
      }
      configuredZone.current = timeZone;
      run({ op: "calendarConfigure", timeZone, firstWeekday: firstWeekdayFromLocale() });
      if (!hadSnapshot.current) {
        run({ op: "calendarSetView", viewMode: "month" });
      }
    };
    configure();
    const onVisible = () => {
      if (document.visibilityState === "visible") {
        configure();
      }
    };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onVisible);
    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onVisible);
    };
  }, [run]);
  // The minute clock: a tick aligned to the next minute boundary, then every
  // minute; skipped while the page is hidden (the foreground path above
  // catches up). Cleared on unmount.
  useEffect(() => {
    let interval: ReturnType<typeof setInterval> | null = null;
    const tick = () => {
      if (document.visibilityState === "visible") {
        run({ op: "calendarRefreshClock" });
      }
    };
    const align = setTimeout(
      () => {
        tick();
        interval = setInterval(tick, CLOCK_TICK_MS);
      },
      CLOCK_TICK_MS - (Date.now() % CLOCK_TICK_MS),
    );
    return () => {
      clearTimeout(align);
      if (interval !== null) {
        clearInterval(interval);
      }
    };
  }, [run]);
  useEffect(() => {
    if (calendar) {
      hadSnapshot.current = true;
    }
  }, [calendar]);

  const showToday = useCallback(() => run({ op: "calendarShowToday" }), [run]);
  const navigate = useCallback((delta: number) => run({ op: "calendarNavigate", delta }), [run]);
  const setView = useCallback(
    (viewMode: CalendarViewMode, anchor?: CalendarDate) =>
      run(
        anchor ? { op: "calendarSetView", viewMode, anchor } : { op: "calendarSetView", viewMode },
      ),
    [run],
  );
  const refresh = useCallback(() => run({ op: "calendarRefresh" }), [run]);
  const toggleCalendar = useCallback(
    (calendarId: string) => run({ op: "calendarToggleCalendar", calendarId }),
    [run],
  );
  const selectOccurrence = useCallback(
    (key: string | null) => run({ op: "calendarSelectOccurrence", key }),
    [run],
  );
  const openDay = useCallback((date: CalendarDate) => setView("day", date), [setView]);
  const closeDetail = useCallback(() => selectOccurrence(null), [selectOccurrence]);
  const toggleCalendars = useCallback(() => {
    if (compact) {
      setSheetOpen((current) => !current);
      return;
    }
    setSidebarOpen((current) => {
      writeSidebarPreference(!current);
      return !current;
    });
  }, [compact]);
  const closeSheet = useCallback(() => setSheetOpen(false), []);

  // Compact: the event sheet and the calendars sheet each hold a history
  // entry so the platform back gesture dismisses them (only while this
  // surface is the active one). A popstate closes what the landed-on entry
  // no longer records.
  const selectedKey = calendar?.selectedOccurrenceKey ?? null;
  useCalendarHistory({
    compact: compact && active,
    eventOpen: selectedKey !== null,
    calendarsOpen: sheetOpen,
    onPop: ({ eventOpen, calendarsOpen }) => {
      if (!calendarsOpen) {
        setSheetOpen(false);
      }
      if (!eventOpen && selectedKey !== null) {
        selectOccurrence(null);
      }
    },
  });
  // Leaving the compact layout (rotation, window resize) drops the sheet.
  useEffect(() => {
    if (!compact) {
      setSheetOpen(false);
    }
  }, [compact]);

  // Keyboard: t today, ← → previous/next, d/w/m/a view modes, Escape closes
  // the detail. Only while this surface is mounted and focus is not in a
  // text field; the mail shortcuts are unmounted with the mail surface.
  const hasCalendar = calendar !== null;
  useEffect(() => {
    if (!hasCalendar) {
      return;
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.metaKey || event.ctrlKey || event.altKey || event.defaultPrevented) {
        return;
      }
      if (isTypingTarget(event.target)) {
        return;
      }
      switch (event.key) {
        case "t":
          event.preventDefault();
          showToday();
          break;
        case "ArrowLeft":
          event.preventDefault();
          navigate(-1);
          break;
        case "ArrowRight":
          event.preventDefault();
          navigate(1);
          break;
        case "d":
          event.preventDefault();
          setView("day");
          break;
        case "w":
          event.preventDefault();
          setView("week");
          break;
        case "m":
          event.preventDefault();
          setView("month");
          break;
        case "a":
          event.preventDefault();
          setView("agenda");
          break;
        case "Escape":
          if (sheetOpen) {
            setSheetOpen(false);
          } else if (selectedKey) {
            closeDetail();
          }
          break;
        default:
          break;
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [hasCalendar, showToday, navigate, setView, closeDetail, selectedKey, sheetOpen]);

  if (!calendar) {
    return (
      <div className="flex min-h-0 flex-1 flex-col" data-calendar-surface>
        <CalendarPlaceholder status={availability === "unavailable" ? "unavailable" : "loading"} />
      </div>
    );
  }

  const synced = calendar.lastSyncedAt ? lastSyncedLabel(calendar.lastSyncedAt) : null;

  return (
    <div
      className="flex min-h-0 flex-1 flex-col"
      data-calendar-surface
      data-view-mode={calendar.viewMode}
    >
      <CalendarToolbar
        snapshot={calendar}
        compact={compact}
        calendarsOpen={compact ? sheetOpen : sidebarOpen}
        onToday={showToday}
        onNavigate={navigate}
        onSetView={setView}
        onRefresh={refresh}
        onToggleCalendars={toggleCalendars}
      />
      {calendar.error ? (
        <div
          role="alert"
          className="flex items-center gap-3 border-b border-destructive/40 bg-destructive/10 px-4 py-2 text-sm text-destructive"
        >
          <span className="min-w-0 flex-1 truncate">{calendar.error}</span>
          <button
            type="button"
            onClick={refresh}
            className="shrink-0 rounded-md border border-destructive/40 px-2 py-0.5 text-xs transition-colors hover:bg-destructive/10"
          >
            Retry
          </button>
        </div>
      ) : null}
      <div className="flex min-h-0 flex-1">
        {!compact && sidebarOpen ? (
          <nav
            aria-label="Calendars"
            className="flex w-56 shrink-0 flex-col overflow-y-auto border-r border-border/70"
          >
            <CalendarList snapshot={calendar} onToggle={toggleCalendar} compact={false} />
            {synced ? (
              <p className="mt-auto px-4 py-3 text-[11px] text-muted-foreground/70">{synced}</p>
            ) : null}
          </nav>
        ) : null}
        <section
          aria-label="Calendar"
          className={cn(
            "flex min-w-0 flex-1 flex-col",
            compact && "pb-[env(safe-area-inset-bottom)]",
          )}
        >
          <CalendarContent
            calendar={calendar}
            compact={compact}
            onSelectDay={openDay}
            onSelectOccurrence={selectOccurrence}
          />
        </section>
        <EventDetail snapshot={calendar} compact={compact} onClose={closeDetail} />
      </div>
      {compact && sheetOpen ? (
        <Sheet title="Calendars" variant="bottom" onClose={closeSheet} testId="calendars">
          <CalendarList snapshot={calendar} onToggle={toggleCalendar} compact />
          {synced ? (
            <p className="px-4 pb-3 text-[11px] text-muted-foreground/70">{synced}</p>
          ) : null}
        </Sheet>
      ) : null}
    </div>
  );
}
